import type { Call } from '@/stores/useCallStore';
import { useCallStore } from '@/stores/useCallStore';
import type { UnwrapRef } from 'vue';
import { type Headset, headsetService } from '@/libraries/headset/headset';

enum SpokesErrorType {
  Unknown = 0,
  Invalid_Uid = 1,
  Exception = 2,
  Invalid_Session = 3,
  Server_Offline = 4,
}

export let pluginRegistered = false;
const pluginName = 'HeadsetPlugin';
let spokes: any;
let SpokesCallId: any;
let SpokesContact: any;
let SessionCallState: { Lookup: { [key: number]: string } };
let isConnected = false;
let pollRate = 2000;
let previousPollRate = 2000;
let run: ReturnType<typeof setInterval>;
let actualCall: UnwrapRef<Call>;

export async function initSpokes(): Promise<Headset | undefined> {
  try {
    if (isConnected) return spokesService;
    //@ts-ignore
    const module = await import('@/libraries/headset/spokes/spokes.js');
    const Spokes = module.default;
    SpokesCallId = module.SpokesCallId;
    SessionCallState = module.SessionCallState;
    SpokesContact = module.SpokesContact;
    // assign to global index variable
    spokes = new Spokes('https://127.0.0.1:32018/Spokes');
    return spokesService;
  } catch (error) {
    console.error('Error while initializing spokes.js', error);
  }
}

function setPluginActive() {
  run = setInterval(pollDeviceEventsOrReconnect, pollRate);
  //Set plugin active status to true
  spokes.Plugin.isActive(pluginName, true, function (result: any) {
    if (!result.isError) {
      pluginRegistered = true;
    }
  });
}

// Register a Spokes Plugin (Plantronics API application session) to get access to Call Services, Device and Call events
function registerPlugin() {
  if (!pluginRegistered) {
    spokes.Plugin.register(pluginName, function (result: any) {
      if (!result.isError) {
        setPluginActive();
      } else {
        if (result.Err.Description === 'Plugin exists') {
          setPluginActive();
        } else {
          isConnected = false;
          disconnectFromPlantronics();
        }
      }
    });
  }
}

// Unregister Spokes plugin (Plantronics API application session)
function unregisterPlugin() {
  spokes.Plugin.unRegister(pluginName);
  pluginRegistered = false;
  clearInterval(run);
}

// Cleanup the Plantronics REST API
export function disconnectFromPlantronics() {
  unregisterPlugin();
  spokes.Device.release();
}

function deviceAttachedCallback(session: any) {
  if (session.isError || !spokes.Device.isAttached) {
    isConnected = false;
    disconnectFromPlantronics();
  } else {
    registerPlugin(); // register a plugin (Plantronics API application session)
  }
}

// Function to perform device and call event polling if we are connected to Hub, or else attempt to reconnect to Hub
function pollDeviceEventsOrReconnect() {
  // supports variable poll rate, 2000ms waiting for a device, 10000ms waiting for Hub to be running
  if (previousPollRate != pollRate) {
    clearInterval(run);
    previousPollRate = pollRate;
    run = setInterval(pollDeviceEventsOrReconnect, pollRate);
  }
  if (spokes == null || !isConnected || !spokes.Device.isAttached) {
    // POLLING FOR HUB / DEVICE RE-ATTACH
    headsetService?.connect();
    return;
  }

  // Poll for device events
  // informs us of a variety of Plantronics device state changes
  spokes.Device.events(function (result: any) {
    if (result.isError) {
      if (result.Err.Description === 'No response.  Server appears to be offline.') {
        pollRate = 10_000;
      }
      if (
        result.Err.Description === 'Invalid session id' ||
        result.Err.Description === 'Empty session id' ||
        result.Err.Description === 'No response.  Server appears to be offline.'
      ) {
        // DEVICE DETACHED / SESSION INVALID
        isConnected = false;
        disconnectFromPlantronics();
      }
    }
  });

  // Poll for call state events (call control events)
  // informs us the calling state has changed, for example user as answered/terminated a call
  // using headset buttons - this event should be used in my app to actually connect/terminate the call!
  spokes.Plugin.callEvents(pluginName, function (result: any) {
    if (result.isError) {
      console.error(result.error);
      // Error polling for call events
      if (result.Err.Description === 'No response.  Server appears to be offline.') {
        pollRate = 10_000;
      }
      if (
        result.Err.Description === 'Invalid session id' ||
        result.Err.Description === 'Empty session id' ||
        result.Err.Description === 'No response.  Server appears to be offline.'
      ) {
        // DEVICE DETACHED / SESSION INVALID
        isConnected = false;
        disconnectFromPlantronics();
      }
    } else {
      if (result.Result.length > 0) {
        const $callStore = useCallStore();
        for (let i = 0; i < result.Result.length; i++) {
          const action = SessionCallState.Lookup[result.Result[i]['Action']];
          if (action === 'AcceptCall') {
            $callStore.acceptIncoming(actualCall);
          } else if (action === 'TerminateCall') {
            $callStore.end(actualCall);
          } else if (action === 'MuteON') {
            $callStore.setMute(actualCall, true);
          } else if (action === 'MuteOFF') {
            $callStore.setMute(actualCall, false);
          }
        }
      }
    }
  });
}

function getCallId(callUuid: any) {
  const parsedCallUuid = parseInt(callUuid.toString().slice(0, 4), 16);
  return new SpokesCallId({ Id: '' + parsedCallUuid });
}

function getContact(contactname: any) {
  return new SpokesContact({ Name: contactname });
}

function disableRinging() {
  spokes.Device.ring(false);
}

export const spokesService: Headset = {
  isConnected: () => isConnected,
  connect(): void {
    spokes.Device.deviceInfo(function (result: any) {
      if (!result.isError) {
        if (result.Result != null) {
          console.log('Device found = ' + result.Result.ProductName + ', id = ' + result.Result.Uid);
          isConnected = true;
          // attach to the device, provide a callback function for the result
          spokes.Device.attach(result.Result.Uid, deviceAttachedCallback);
        } else {
          isConnected = false;
        }
        pollRate = 2000; // waiting for device events now, force faster polling rate to start now (if applicable)
        if (previousPollRate == 10000) {
          previousPollRate = 2000;
          // start polling the device and call state events
          run = setInterval(pollDeviceEventsOrReconnect, pollRate);
        }
      } else {
        if (result.Err.Type === SpokesErrorType.Server_Offline) {
          console.error(`Could not connect with Platronics Hub.`);
        } else if (result.Err.Description === 'There are no supported devices') {
          console.error('There are no supported devices');
          // Please attach a Plantronics headset to the PC
        } else {
          pollRate = 10_000; // slow down polling rate while we are waiting for Hub to be running
        }
      }
    });
  },
  disconnect(): void {
    if (isConnected) {
      isConnected = false;
      disconnectFromPlantronics();
    }
  },
  incomingCall(call: UnwrapRef<Call>) {
    actualCall = call;
    spokes.Plugin.incomingCall(pluginName, getCallId(call.callId), getContact('Cloud Pure Contact'), 'Unknown', 'ToHeadset', function () {
      disableRinging();
    });
  },

  answerCall(callUuid: string) {
    spokes.Plugin.answerCall(pluginName, getCallId(callUuid));
  },

  endCall(callUuid: string) {
    spokes.Plugin.terminateCall(pluginName, getCallId(callUuid));
  },

  muteCall(mute: boolean) {
    spokes.Plugin.muteCall(pluginName, mute);
  },
};
